import { NodeEvaluationStatus, NodeId } from '../../types';
import {
  GraphBatchClaim,
  GraphBatchCompiler,
  GraphBatchEvaluator,
  GraphBatchExecutionOutcome,
  GraphBatchGateway,
} from './contracts';
import { NodeEvalResult, NodeEvalStatus } from './types';
import { GraphBatchPipelineInstance } from './graph-batch-pipeline';
import { match } from 'ts-pattern';

type GraphBatchExecutorArgs = {
  claim: GraphBatchClaim;
  gateway: GraphBatchGateway;
  compiler: GraphBatchCompiler;
  evaluator: GraphBatchEvaluator;
};

/**
 * Executes one batch envelope using a deterministic pipeline instance.
 */
export class GraphBatchExecutor {
  private readonly claim: GraphBatchClaim;
  private readonly gateway: GraphBatchGateway;
  private readonly compiler: GraphBatchCompiler;
  private readonly evaluator: GraphBatchEvaluator;

  constructor({ claim, gateway, compiler, evaluator }: GraphBatchExecutorArgs) {
    this.claim = claim;
    this.gateway = gateway;
    this.compiler = compiler;
    this.evaluator = evaluator;
  }

  async run(isCancelled: () => boolean): Promise<GraphBatchExecutionOutcome> {
    const batch = this.claim.batch;
    const snapshot = this.gateway.getGraphSnapshot(batch.graphId);
    const pipeline = new GraphBatchPipelineInstance(batch, snapshot.dag);

    if (snapshot.graphVersion !== batch.graphVersion) {
      return { kind: 'stale' };
    }

    while (!isCancelled() && pipeline.hasWork()) {
      if (this.isStale()) {
        return { kind: 'stale' };
      }

      const wave = pipeline.takeReadyWave();
      const partition = pipeline.partitionWave(wave, (nodeId) =>
        this.canRunNode(batch.graphId, pipeline, nodeId)
      );

      this.applyBlockedNodes(batch.graphId, pipeline, partition.blocked);

      if (partition.runnable.length === 0) {
        continue;
      }

      pipeline.markRunning(partition.runnable);
      this.gateway.markNodesRunning(batch.graphId, partition.runnable);

      const requests = partition.runnable.map((nodeId) =>
        this.compiler.compileNode({
          batch,
          graph: snapshot.graph,
          nodeId,
        })
      );
      requests.forEach((request) => {
        this.gateway.setNodeTemplate(
          batch.graphId,
          request.nodeId,
          request.rawTemplate
        );
      });

      let results: NodeEvalResult[];
      try {
        results = await this.evaluator.evaluateWave({
          entryId: batch.entryId,
          reqs: requests.map((request) => ({
            nodeId: request.nodeId,
            template: request.template,
          })),
        });
      } catch (error) {
        pipeline.clearInFlight();
        this.applyWaveError(
          batch.graphId,
          pipeline,
          partition.runnable,
          error instanceof Error
            ? error.message
            : 'Failed to evaluate graph wave'
        );
        return { kind: 'failed', error };
      }

      pipeline.clearInFlight();

      if (isCancelled()) {
        return { kind: 'cancelled' };
      }
      if (this.isStale()) {
        return { kind: 'stale' };
      }

      if (results.length === 0 && partition.runnable.length > 0) {
        this.applyWaveError(
          batch.graphId,
          pipeline,
          partition.runnable,
          'Evaluator returned no results for active wave'
        );
        return { kind: 'failed', error: new Error('empty wave response') };
      }

      this.applyResults(batch.graphId, batch, pipeline, results);
    }

    if (isCancelled()) {
      return { kind: 'cancelled' };
    }

    return { kind: 'completed' };
  }

  private isStale(): boolean {
    return (
      this.gateway.getGraphVersion(this.claim.batch.graphId) !==
      this.claim.batch.graphVersion
    );
  }

  private canRunNode(
    graphId: string,
    pipeline: GraphBatchPipelineInstance,
    nodeId: NodeId
  ): boolean {
    const node = pipeline.dag[nodeId];
    if (!node) return false;
    if (!node.children.length) return true;

    return node.children.every((childId) => {
      const childEval = this.gateway.getNodeEvaluation(graphId, childId);
      // A parent is runnable only when each connected input dependency has a valid value.
      // Nodes can be `Idle` after being skipped due to an upstream failure, so checking
      // only for `Error` allows stale/invalid parent evaluation.
      return childEval?.status === NodeEvaluationStatus.Ok;
    });
  }

  private applyBlockedNodes(
    graphId: string,
    pipeline: GraphBatchPipelineInstance,
    nodeIds: NodeId[]
  ) {
    if (nodeIds.length === 0) return;

    const skipped = new Set<NodeId>();
    nodeIds.forEach((nodeId) => {
      pipeline.markSkipped(nodeId).forEach((skippedNodeId) => {
        skipped.add(skippedNodeId);
      });
    });

    if (skipped.size === 0) return;
    this.gateway.applyNodesSkipped(graphId, Array.from(skipped));
  }

  private applyWaveError(
    graphId: string,
    pipeline: GraphBatchPipelineInstance,
    nodeIds: NodeId[],
    message: string
  ) {
    const skipped = new Set<NodeId>();

    nodeIds.forEach((nodeId) => {
      this.gateway.applyNodeError(graphId, nodeId, message);
      pipeline.markFailure(nodeId).forEach((ancestorId) => {
        skipped.add(ancestorId);
      });
    });

    if (skipped.size === 0) return;
    this.gateway.applyNodesSkipped(graphId, Array.from(skipped));
  }

  private applyResults(
    graphId: string,
    batch: GraphBatchClaim['batch'],
    pipeline: GraphBatchPipelineInstance,
    results: NodeEvalResult[]
  ) {
    const skipped = new Set<NodeId>();

    for (const result of results) {
      match(result)
        .with({ status: NodeEvalStatus.Ok }, (result) => {
          this.gateway.applyNodeSuccess(graphId, result);
          pipeline.markSuccess(result.nodeId);
        })
        .with({ status: NodeEvalStatus.Error }, (result) => {
          this.gateway.applyNodeError(graphId, result.nodeId, result.message);
          pipeline.markFailure(result.nodeId).forEach((ancestorId) => {
            skipped.add(ancestorId);
          });
        })
        .with({ status: NodeEvalStatus.Skipped }, (result) => {
          pipeline.markSkipped(result.nodeId).forEach((skippedNodeId) => {
            skipped.add(skippedNodeId);
          });
        })
        .exhaustive();

      this.gateway.applyTerminalResult(batch, result);
    }

    if (skipped.size === 0) return;
    this.gateway.applyNodesSkipped(graphId, Array.from(skipped));
  }
}
